import type { Match } from '@uefadigital/panenka';
import dayjs from 'dayjs';
import { useMemo, useEffect } from 'react';
import { Outlet, useLocation, useParams } from 'react-router';

import { useGetMatchByIdQuery } from 'api/fspApi';
import { MatchHeader } from 'components/Match/MatchHeader';
import { PrintMatchPdfTab } from 'components/Match/PrintMatchPdfTab';
import { Sponsors } from 'components/Sponsors';
import usePrintMode from 'hooks/usePrintMode';
import { routes } from 'routes';
import Notfound from 'routes/notfound';
import { publicPath } from 'signals/publicPath';
import config from 'utils/config';
import { isMatchValid } from 'utils/helpers';

type MatchParams = {
  competitionName: string;
  seasonYear: string;
  matchId: string;
};

const MatchRoute = () => {
  const { competitionName, seasonYear: urlSeasonYear, matchId } = useParams<MatchParams>();
  const isPrintMode = usePrintMode();
  const competition = config.competitions.find((item) => item.name === competitionName) || config.competitionsDefault;
  const seasonYear = urlSeasonYear ?? dayjs().year().toString();

  const { data: match, error: matchIsError, isLoading: matchLoading } = useGetMatchByIdQuery(matchId!);

  const PUBLIC_PATH = publicPath.value;
  const matchPath = `${PUBLIC_PATH}competitions/:competitionName/:seasonYear/match/:matchId`;
  const location = useLocation();
  useEffect(() => {
    if (!isPrintMode && location.pathname === `${matchPath}/${routes.print}`) {
      window.print();
    }
  }, [isPrintMode, location, matchPath]);

  const tabProps = useMemo(() => {
    return {
      competition,
      matchData: match as Match,
      seasonYear,
      matchId: matchId!,
    };
  }, [competition, match, seasonYear, matchId]);

  useEffect(() => {
    if (!competition) return;
    import(
      `../../../node_modules/@uefadigital/panenka-base/dist/css/competitions/${competition.shortName}/${competition.shortName}.css`
    );
  }, [competition]);

  if (matchLoading) {
    return <div style={{ height: '100%' }} />;
  }

  if (matchIsError || !match || !isMatchValid(match)) {
    return <Notfound />;
  }

  return (
    <>
      <div pk-competition={competition.shortName} pk-theme="light" className="pk-bg--background pr-display--none">
        <div pk-theme="dark">
          <MatchHeader matchData={match} competitionConfig={competition} seasonYear={seasonYear} matchId={matchId!} />
        </div>
        <Outlet />
        <Sponsors matchData={match} />
      </div>
      <div className="pk-d--none pr-display--block">
        <PrintMatchPdfTab {...tabProps} />
      </div>
    </>
  );
};

export default MatchRoute;
